import { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation, Navigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { ActionTypes } from '../constants/actionTypes';
import { setAuth0TokenGetter } from '../apis/ComponentApis';
import { jwtDecode } from 'jwt-decode';
import * as action from '../actions/index.js';

function hasAny(list = [], required = []) {
  if (!required?.length) return true;
  const set = new Set(list);
  return required.some((p) => set.has(p));
}

function hasAll(list = [], required = []) {
  if (!required?.length) return true;
  const set = new Set(list);
  return required.every((p) => set.has(p));
}

const PrivateRoute = ({ children, anyOf = [], allOf = [], requiredPermissions = [] }) => {
  const {
    loginWithRedirect,
    user,
    isAuthenticated,
    isLoading,
    getAccessTokenSilently,
  } = useAuth0();
  const dispatch = useDispatch();
  const location = useLocation();
  const [permChecked, setPermChecked] = useState(false);
  const [hasPermission, setHasPermission] = useState(false);

  useEffect(() => {
    if (user !== undefined && isAuthenticated) {
      dispatch({ type: ActionTypes.SET_AUTHORIZATION_USERINFO, payload: user });
    }
  }, [user, isAuthenticated, dispatch]);

  useEffect(() => {
    setAuth0TokenGetter(getAccessTokenSilently);
  }, [getAccessTokenSilently]);

  // Get token & check permissions
  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      if (!isAuthenticated) return;

      try {
        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: process.env.REACT_APP_AUDIENCE,
            scope: 'openid profile email offline_access',
            //organization: process.env.REACT_APP_ORGANIZATION_NAME,
          },
        });
        const payload = jwtDecode(token);

        if (payload) {
          const NS = `https://${payload.orgName || process.env.REACT_APP_NAMESPACE}.com`;
          const roles = payload[`${NS}/roles`];
          try {
            dispatch(action.setUserRole(roles));
          } catch (err) {
            console.warn('Failed to store roles in sessionStorage:', err);
          }
        } else {
          console.warn('Access token is not a JWT or failed to decode.');
        }

        const tokenPermissions = payload?.permissions || [];
        const combined = [...new Set(tokenPermissions || [])];

        try {
          dispatch(action.setUserPermission(combined));
        } catch (err) {
          console.warn('Failed to store permissions in sessionStorage:', err);
        }

        const anyList = anyOf?.length ? anyOf : requiredPermissions || [];
        const allowed = hasAny(combined, anyList) && hasAll(combined, allOf);

        if (!cancelled) {
          setHasPermission(allowed);
          setPermChecked(true);
        }
      } catch (err) {
        if (err?.error_description?.includes('invalid refresh token')) {
          await loginWithRedirect({
            appState: { returnTo: window.location.pathname },
            authorizationParams: {
              scope: 'openid profile email offline_access',
              organization: process.env.REACT_APP_ORGANIZATION_NAME || undefined,
            },
          });
        } else {
          console.error('Token/permission check failed:', err);
          if (!cancelled) {
            setHasPermission(false);
            setPermChecked(true);
          }
        }
      }
    };

    check();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, getAccessTokenSilently, loginWithRedirect, user, requiredPermissions]);

  if (isLoading || (isAuthenticated && !permChecked)) {
    return <>Loading...</>;
  }
  if (!isAuthenticated) {
    loginWithRedirect({
      appState: { returnTo: location.pathname },
      authorizationParams: {
        scope: 'openid profile email offline_access',
        organization: process.env.REACT_APP_ORGANIZATION_NAME || undefined
      }
    });
    return null;
  }
  if (!hasPermission) {
    return <Navigate to="/unauthorized" replace state={{ from: location.pathname }} />;
  }
  return children;
};

export default PrivateRoute;